import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'

export interface CartItem {
  produk_id: string
  nama_produk: string
  harga: number
  harga_max?: number | null
  qty: number
  gambar_url?: string
  satuan: string
  jumlah_hari?: number
  tanggal_mulai?: string
}

export interface OrderData {
  nama_pemesan: string
  alamat: string
  no_hp?: string
  tanggal_kirim: string
  jam_kirim: string
  metode_bayar: 'tunai' | 'transfer'
  rekening_id?: string
}

interface CartContextType {
  items: CartItem[]
  orderData: OrderData | null
  setOrderData: (data: OrderData) => void
  addItem: (item: CartItem) => void
  removeItem: (produk_id: string) => void
  updateQty: (produk_id: string, qty: number) => void
  getSubtotal: () => number
  clearCart: () => void
  isLoading: boolean
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  const [orderData, setOrderData] = useState<OrderData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load cart dari localStorage lalu re-validasi harga dari DB
  useEffect(() => {
    async function loadAndValidate() {
      try {
        const savedItems = localStorage.getItem('cart_items')
        const savedOrderData = localStorage.getItem('cart_order_data')

        if (savedOrderData) setOrderData(JSON.parse(savedOrderData))

        if (!savedItems) { setIsLoading(false); return }

        const parsed: CartItem[] = JSON.parse(savedItems)
        const validated = parsed
          .filter(item => item.produk_id && item.nama_produk)
          .map(item => ({
            ...item,
            harga: typeof item.harga === 'number' ? item.harga : 0,
            qty: typeof item.qty === 'number' && item.qty > 0 ? item.qty : 1,
          }))

        if (validated.length === 0) { setIsLoading(false); return }

        // Re-fetch harga terbaru dari DB
        const ids = validated.map(i => i.produk_id)
        const { data: produkData } = await supabase
          .from('produk')
          .select('id, harga, harga_max')
          .in('id', ids)

        if (produkData && produkData.length > 0) {
          const priceMap: Record<string, { harga: number; harga_max: number | null }> = {}
          for (const p of produkData) priceMap[p.id] = { harga: p.harga, harga_max: p.harga_max ?? null }

          const refreshed = validated.map(item => ({
            ...item,
            harga: priceMap[item.produk_id]?.harga ?? item.harga,
            harga_max: priceMap[item.produk_id]?.harga_max ?? null,
          }))
          setItems(refreshed)
        } else {
          setItems(validated)
        }
      } catch (error) {
        console.error('Error loading cart:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadAndValidate()
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart_items', JSON.stringify(items))
  }, [items])

  useEffect(() => {
    localStorage.setItem('cart_order_data', JSON.stringify(orderData))
  }, [orderData])

  const addItem = (item: CartItem) => {
    setItems(prev => {
      const existing = prev.find(i => i.produk_id === item.produk_id)
      if (existing) {
        return prev.map(i =>
          i.produk_id === item.produk_id
            ? { ...i, qty: i.qty + item.qty }
            : i
        )
      }
      return [...prev, item]
    })
  }

  const removeItem = (produk_id: string) => {
    setItems(prev => prev.filter(i => i.produk_id !== produk_id))
  }

  const updateQty = (produk_id: string, qty: number) => {
    if (qty <= 0) {
      removeItem(produk_id)
    } else {
      setItems(prev =>
        prev.map(i =>
          i.produk_id === produk_id ? { ...i, qty } : i
        )
      )
    }
  }

  const getSubtotal = () => {
    return items.reduce((sum, item) => {
      const price = typeof item.harga === 'number' ? item.harga : 0
      const quantity = typeof item.qty === 'number' ? item.qty : 0
      const days = typeof item.jumlah_hari === 'number' && item.jumlah_hari > 0 ? item.jumlah_hari : 1
      return sum + (price * quantity * days)
    }, 0)
  }

  const clearCart = () => {
    setItems([])
    setOrderData(null)
  }

  return (
    <CartContext.Provider
      value={{
        items,
        orderData,
        setOrderData,
        addItem,
        removeItem,
        updateQty,
        getSubtotal,
        clearCart,
        isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within CartProvider')
  }
  return context
}
